import React from 'react';
import { useLocation } from 'react-router-dom';
import TabbedImageUploadModal from './TabbedImageUploadModal';
import { useWorkbookImageUpload } from '@/hooks/content-generator/use-workbook-images';

interface WorkbookImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageUploaded: (imageUrl: string, altText?: string, imageId?: string) => void; // NEW: Add optional imageId parameter
  workbookId: string;
  pageId?: string;
  initialDescription?: string; // NEW: Pre-fill description with AI-generated prompt
  onDirectImageImport?: (imageUrl: string, altText?: string) => void; // NEW: Direct URL insertion callback for editor contexts
}

const WorkbookImageUploadModal: React.FC<WorkbookImageUploadModalProps> = ({
  isOpen,
  onClose,
  onImageUploaded,
  workbookId,
  pageId,
  initialDescription = '', // NEW: Default to empty string
  onDirectImageImport, // NEW: Direct URL insertion callback
}) => {
  const location = useLocation();
  const uploadMutation = useWorkbookImageUpload();

  // Determine if Import tab should be shown based on current page
  const showImportTab = React.useMemo(() => {
    const pathname = location.pathname;
    
    // Show Import tab on these specific pages:
    // - /storage
    // - /private-text-generator 
    // - /project/{id}/pages
    return (
      pathname === '/storage' ||
      pathname === '/private-text-generator' ||
      pathname.includes('/project/') && pathname.endsWith('/pages')
    );
  }, [location.pathname]);

  const handleUpload = async (file: File): Promise<string> => {
    try {
      // Check for generation metadata attached to file
      const metadata = (file as any).generationMetadata;
      
      console.log('[WorkbookImageUploadModal] Upload with metadata:', metadata);
      
      const uploadedImage = await uploadMutation.mutateAsync({
        file,
        workbookId,
        pageId,
        altText: metadata?.altText || 'Workbook image',
        isGenerated: metadata?.isGenerated || false,
        generationPrompt: metadata?.generationPrompt,
      });

      // Call the callback with the uploaded image URL, alt text, and ID
      onImageUploaded(uploadedImage.image_url, uploadedImage.alt_text, uploadedImage.id);
      
      // Return the URL as expected by the interface
      return uploadedImage.image_url;
    } catch (error) {
      console.error('Error uploading workbook image:', error);
      // Error handling is done in the hook via toast
      throw error;
    }
  };

  // Default direct import handler for non-editor contexts
  // Treats imported images the same as uploaded images (calls onImageUploaded)
  const handleDirectImageImport = onDirectImageImport || ((imageUrl: string, altText?: string) => {
    console.log('[WorkbookImageUploadModal] Direct import - treating as uploaded image:', { imageUrl, altText });
    // For project contexts, treat imported images the same as uploaded images
    // This triggers navigation and other upload completion logic
    onImageUploaded(imageUrl, altText, undefined); // No imageId for imports since no new DB entry
    onClose();
  });

  return (
    <TabbedImageUploadModal
      isOpen={isOpen}
      onClose={onClose}
      onUpload={handleUpload}
      title="✨ AI Image Magic"
      description="Upload or generate an image for your workbook content"
      maxSizeMB={10} // 10MB - WebP conversion handles optimization
      allowedTypes={['image/jpeg', 'image/png', 'image/webp', 'image/gif']}
      aspectRatio={16/9}
      accentColor="purple"
      autoUpload={true}
      mode="workbook-image"
      skipPreview={true} // Skip preview for seamless AI Magic flow
      showImportTab={showImportTab} // NEW: Conditionally show Import tab based on page context
      // Pass workbook context for generation
      workbookId={workbookId}
      pageId={pageId}
      initialDescription={initialDescription} // NEW: Pass through initial description
      onDirectImageImport={handleDirectImageImport} // UNIFIED: All contexts have direct import support
    />
  );
};

export default WorkbookImageUploadModal; 